import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAppSettings } from "../context/AppSettingsContext";
import { db } from "../firebase";
import { ref, get, update, onValue } from "firebase/database";
import {
  Search, ShieldCheck, BookOpen, Laptop, Bike, Home as HomeIcon,
  Shirt, Dumbbell, Music, PenTool, Sofa, UtensilsCrossed, Palette,
  MoreHorizontal, ArrowLeftRight, FileText, HelpCircle, Car,
  Calendar, GraduationCap, ClipboardList, Gift, Bell, ChevronRight, Users,
  Flame, Star, Zap
} from "lucide-react";

const CATEGORIES = [
  { name: "Books", icon: BookOpen, color: "bg-blue-100 text-blue-600" },
  { name: "Electronics", icon: Laptop, color: "bg-purple-100 text-purple-600" },
  { name: "Cycles & Vehicles", icon: Bike, color: "bg-green-100 text-green-600" },
  { name: "Room Essentials", icon: HomeIcon, color: "bg-orange-100 text-orange-600" },
  { name: "Clothing & Uniforms", icon: Shirt, color: "bg-pink-100 text-pink-600" },
  { name: "Sports & Fitness", icon: Dumbbell, color: "bg-red-100 text-red-600" },
  { name: "Musical Instruments", icon: Music, color: "bg-yellow-100 text-yellow-600" },
  { name: "Study Tools", icon: PenTool, color: "bg-indigo-100 text-indigo-600" },
  { name: "Furniture", icon: Sofa, color: "bg-amber-100 text-amber-600" },
  { name: "Kitchen Items", icon: UtensilsCrossed, color: "bg-teal-100 text-teal-600" },
  { name: "Art & Craft", icon: Palette, color: "bg-rose-100 text-rose-600" },
  { name: "Other", icon: MoreHorizontal, color: "bg-gray-100 text-gray-600" },
];

const QUICK_ACTIONS = [
  { label: "Swap Items", icon: ArrowLeftRight, color: "bg-blue-50 text-blue-600 border-blue-100", path: "/swap" },
  { label: "Buy/Sell Notes", icon: FileText, color: "bg-yellow-50 text-yellow-600 border-yellow-100", path: "/notes" },
  { label: "Lost & Found", icon: HelpCircle, color: "bg-orange-50 text-orange-600 border-orange-100", path: "/lost-found" },
  { label: "Rideshare", icon: Car, color: "bg-green-50 text-green-600 border-green-100", path: "/rideshare" },
  { label: "Events", icon: Calendar, color: "bg-purple-50 text-purple-600 border-purple-100", path: "/events" },
  { label: "Tutoring", icon: GraduationCap, color: "bg-pink-50 text-pink-600 border-pink-100", path: "/tutoring" },
  { label: "Requests", icon: ClipboardList, color: "bg-red-50 text-red-600 border-red-100", path: "/requests" },
  { label: "Free Items", icon: Gift, color: "bg-teal-50 text-teal-600 border-teal-100", path: "/giveaway" },
  { label: "Parent Zone", icon: Users, color: "bg-amber-50 text-amber-600 border-amber-100", path: "/parent-zone" },
];

function ListingCard({ listing }) {
  return (
    <Link to={`/listing/${listing.id}`} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition block">
      <div className="h-32 bg-gray-100 overflow-hidden relative">
        {listing.imageUrl ? (
          <img src={listing.imageUrl} alt={listing.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl text-gray-200">
            <BookOpen size={36} className="text-gray-200" />
          </div>
        )}
        {listing.isRental && (
          <span className="absolute top-1.5 left-1.5 bg-teal-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">🏠 Rent</span>
        )}
      </div>
      <div className="p-3">
        <p className="font-semibold text-gray-800 text-sm line-clamp-1">{listing.title}</p>
        <div className="mt-0.5">
          {listing.isRental && listing.rental ? (
            <div className="flex flex-wrap gap-1 items-center">
              {listing.rental.pricePerDay && <span className="text-xs font-bold text-teal-600">₹{listing.rental.pricePerDay}/day</span>}
              {listing.rental.pricePerWeek && <span className="text-xs font-semibold text-teal-500">₹{listing.rental.pricePerWeek}/wk</span>}
              {listing.rental.pricePerMonth && <span className="text-xs font-semibold text-teal-500">₹{listing.rental.pricePerMonth}/mo</span>}
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <p className={`font-bold text-sm ${listing.isRental ? "text-teal-600" : "text-blue-600"}`}>
                {listing.price === 0 ? "Free" : `₹${listing.price}`}
              </p>
              {listing.isRental && listing.rentalPeriod && (
                <span className="text-xs text-teal-600 font-medium">
                  {{ per_day: "/day", per_week: "/wk", per_month: "/mo" }[listing.rentalPeriod] || ""}
                </span>
              )}
            </div>
          )}
        </div>
        {/* Seller avatar + name */}
        <div className="flex items-center gap-1.5 mt-1.5">
          <div className="w-4 h-4 rounded-full bg-blue-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
            {listing.sellerPhotoURL
              ? <img src={listing.sellerPhotoURL} alt={listing.sellerName} className="w-full h-full object-cover" />
              : <span className="text-blue-600 font-bold text-[8px]">{listing.sellerName?.[0]?.toUpperCase() || "?"}</span>}
          </div>
          <p className="text-gray-400 text-xs truncate">{listing.sellerName || listing.college}</p>
        </div>
      </div>
    </Link>
  );
}

function SwapCard({ swap }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 min-w-56 border border-gray-100">
      <div className="flex items-center gap-1 mb-2">
        <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">SWAP</span>
      </div>
      <p className="font-semibold text-gray-800 text-sm line-clamp-1">{swap.offeringTitle}</p>
      <div className="flex items-center gap-1 my-1.5">
        <ArrowLeftRight size={12} className="text-gray-400" />
        <p className="text-xs text-gray-500 line-clamp-1">Wants: {swap.wantTitle}</p>
      </div>
      <p className="text-xs text-gray-400 mb-3">{swap.postedByName}</p>
      {swap.whatsapp && (
        <a
          href={`https://wa.me/91${swap.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi! I saw your swap offer on CampusSwap. You're offering "${swap.offeringTitle}" and want "${swap.wantTitle}". I'm interested!`)}`}
          target="_blank"
          rel="noreferrer"
          className="block text-center bg-green-500 text-white text-xs py-1.5 rounded-lg font-medium hover:bg-green-600"
        >
          Propose Swap
        </a>
      )}
    </div>
  );
}

export default function Home() {
  const { currentUser, userProfile } = useAuth();
  const { settings } = useAppSettings();
  const { features } = settings;
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [giveaways, setGiveaways] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [appStats, setAppStats] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [loadingListings, setLoadingListings] = useState(true);
  const [allListings, setAllListings] = useState([]);
  const [urgentListings, setUrgentListings] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [rentalListings, setRentalListings] = useState([]);
  const [dailyDeal, setDailyDeal] = useState(null);
  const [soldThisWeekCount, setSoldThisWeekCount] = useState(0);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // Show tutorial once per account — localStorage (instant) + Firebase (cross-device)
  useEffect(() => {
    if (!currentUser) return;
    // Check localStorage first (works even if Firebase rules aren't set)
    const localSeen = localStorage.getItem(`cs_tutorial_seen_${currentUser.uid}`);
    if (localSeen) return;
    if (userProfile === undefined) return; // still loading profile
    if (!userProfile?.tutorialSeen) {
      setShowTutorial(true);
    }
  }, [currentUser, userProfile]);

  async function closeTutorial() {
    setShowTutorial(false);
    setTutorialStep(0);
    if (currentUser) {
      // Save to localStorage immediately (guaranteed to work)
      localStorage.setItem(`cs_tutorial_seen_${currentUser.uid}`, "true");
      // Also save to Firebase for cross-device persistence
      try {
        await update(ref(db, `users/${currentUser.uid}`), { tutorialSeen: true });
      } catch (e) { /* non-critical */ }
    }
  }

  const TUTORIAL_STEPS = [
    { emoji: "👋", title: "Welcome to CampusSwap!", desc: "India's marketplace for verified college students. Buy, sell and swap with students from your own campus." },
    { emoji: "📸", title: "Post a Listing", desc: "Tap the + button at the bottom to sell something. Add a photo, set a price and your WhatsApp number. Takes 30 seconds!" },
    { emoji: "💬", title: "Contact Sellers", desc: "See something you like? Tap it and chat directly on WhatsApp. No middleman, no commission." },
    { emoji: "🔄", title: "Explore Features", desc: "Use the bottom tabs to find Swaps, Lost & Found, Rideshare, Events, Tutoring and more — all free!" },
    { emoji: "✅", title: "Get Verified", desc: "Submit your college ID from your Profile to get a verified badge. Verified users get more trust from buyers." },
  ];

  // Zone detection
  const isParent = userProfile?.userType === "parent";
  const isExamOnly = userProfile?.examAspirant === true;

  // Real-time listings listener
  useEffect(() => {
    setLoadingListings(true);
    const listingsRef = ref(db, "listings");
    const unsub = onValue(listingsRef, (snap) => {
      try {
        if (snap.exists()) {
          const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
          const active = all.filter(l => l.status === "active").sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setAllListings(active);
          setListings(active.slice(0, 6));
          setUrgentListings(active.filter(l => l.urgent === true).slice(0, 8));
          setFeaturedListings(active.filter(l => l.featured === true).slice(0, 6));
          setRentalListings(active.filter(l => l.isRental === true).slice(0, 8));
          const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          const userCollege = userProfile?.college;
          const soldWeek = all.filter(l => {
            const t = l.createdAt ? new Date(l.createdAt).getTime() : 0;
            return l.status === "sold" && t >= oneWeekAgo && (!userCollege || l.college === userCollege);
          });
          setSoldThisWeekCount(soldWeek.length);
          // Load recently viewed
          if (currentUser) {
            try {
              const key = `cs_recently_viewed_${currentUser.uid}`;
              const ids = JSON.parse(localStorage.getItem(key) || "[]");
              if (ids.length > 0) {
                const allData = snap.val();
                const viewed = ids.map(vid => allData[vid] ? { id: vid, ...allData[vid] } : null).filter(Boolean);
                setRecentlyViewed(viewed.slice(0, 6));
              }
            } catch (e) {}
          }
        } else {
          setAllListings([]);
          setListings([]);
          setUrgentListings([]);
          setFeaturedListings([]);
          setRentalListings([]);
        }
      } catch (err) { /* silent */ }
      setLoadingListings(false);
    }, (err) => {
      console.error("Listings listener error:", err);
      setLoadingListings(false);
    });
    return () => unsub();
  }, [userProfile?.college, currentUser]); // eslint-disable-line

  // One-time fetches for swaps, giveaways, appStats, dailyDeal
  useEffect(() => {
    async function fetchSupplementary() {
      // Daily Deal
      try {
        const ddSnap = await get(ref(db, "dailyDeal/current"));
        if (ddSnap.exists()) {
          const ddData = ddSnap.val();
          if (ddData.listingId) {
            const lSnap = await get(ref(db, `listings/${ddData.listingId}`));
            if (lSnap.exists()) setDailyDeal({ id: ddData.listingId, ...lSnap.val() });
          }
        }
      } catch (err) { /* silent */ }

      try {
        const sSnap = await get(ref(db, "swaps"));
        if (sSnap.exists()) {
          const all = Object.entries(sSnap.val()).map(([id, data]) => ({ id, ...data }));
          setSwaps(all.filter(s => s.status === "open").sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 4));
        }
      } catch (err) {}

      try {
        const gSnap = await get(ref(db, "giveaways"));
        if (gSnap.exists()) {
          const all = Object.entries(gSnap.val()).map(([id, data]) => ({ id, ...data }));
          setGiveaways(all.filter(g => g.status === "active").sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 4));
        }
      } catch (err) {}
    }
    fetchSupplementary();
    get(ref(db, "appStats")).then(s => { if (s.exists()) setAppStats(s.val()); }).catch(() => {});
  }, []);

  // "For You" personalized feed from recently viewed + saved categories
  const forYouListings = useMemo(() => {
    if (!allListings.length || !currentUser) return [];
    const cats = [...new Set(recentlyViewed.filter(l => l.category).map(l => l.category))];
    if (!cats.length) return [];
    const viewedIds = new Set(recentlyViewed.map(l => l.id));
    return allListings
      .filter(l => cats.includes(l.category) && l.sellerId !== currentUser.uid && !viewedIds.has(l.id))
      .slice(0, 10);
  }, [allListings, recentlyViewed, currentUser]);

  function handleSearch(e) {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/browse?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  }

  async function handleShareApp() {
    const shareData = {
      title: "CampusSwap – Campus Marketplace",
      text: `Hey! Buy, sell & swap stuff with students on CampusSwap 🎓 It's free!\n`,
      url: "https://campusswap.in",
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (e) {}
    } else {
      const wa = `https://wa.me/?text=${encodeURIComponent(shareData.text + shareData.url)}`;
      window.open(wa, "_blank");
    }
  }

  const visibleActions = QUICK_ACTIONS.filter(a => {
    const map = { "/swap": "swap", "/notes": "notes", "/lost-found": "lostFound", "/rideshare": "rideshare", "/events": "events", "/tutoring": "tutoring", "/requests": "requests", "/giveaway": "giveaway", "/parent-zone": "parentZone" };
    const key = map[a.path];
    return key ? features[key] !== false : true;
  });

  return (
    <div className="pb-20 bg-gray-50 min-h-screen">

      {/* Onboarding Tutorial */}
      {showTutorial && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center p-4 pb-24">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl">
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">{TUTORIAL_STEPS[tutorialStep].emoji}</div>
              <h2 className="text-lg font-bold text-gray-800">{TUTORIAL_STEPS[tutorialStep].title}</h2>
              <p className="text-gray-500 text-sm mt-2">{TUTORIAL_STEPS[tutorialStep].desc}</p>
            </div>
            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mb-5">
              {TUTORIAL_STEPS.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all ${i === tutorialStep ? "w-6 bg-blue-600" : "w-1.5 bg-gray-200"}`} />
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={closeTutorial}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-50">
                Skip
              </button>
              <button onClick={() => {
                if (tutorialStep < TUTORIAL_STEPS.length - 1) setTutorialStep(s => s + 1);
                else closeTutorial();
              }}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700">
                {tutorialStep < TUTORIAL_STEPS.length - 1 ? "Next →" : "Let's go!"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Welcome Message */}
      {settings.welcomeMessage && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3 text-sm text-yellow-800 font-medium">
          {settings.welcomeMessage}
        </div>
      )}

      {/* Welcome Banner */}
      <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-600 px-4 pt-4 pb-14 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-10 translate-x-10" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-8 -translate-x-6" />
        <div className="flex items-start justify-between relative">
          <div>
            <p className="text-blue-200 text-xs mb-0.5">👋 Welcome back,</p>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold">{userProfile?.name?.split(" ")[0] || "Student"}</h2>
              {userProfile?.verified && <ShieldCheck size={16} className="text-green-300" />}
            </div>
            <p className="text-blue-200 text-xs mt-0.5 line-clamp-1">📍 {userProfile?.college || "Your Campus"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleShareApp} className="p-2.5 bg-white/20 rounded-2xl hover:bg-white/30 backdrop-blur-sm" title="Invite Friends">
              <Users size={18} />
            </button>
            <Link to="/notice-board" className="p-2.5 bg-white/20 rounded-2xl hover:bg-white/30 backdrop-blur-sm">
              <Bell size={18} />
            </Link>
          </div>
        </div>
      </div>

      {/* Search Bar — tapping navigates to Browse for search */}
      <div className="px-4 -mt-6 mb-4 relative z-20">
        <form onSubmit={handleSearch} className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => navigate("/browse")}
            placeholder="Search books, electronics, cycles..."
            className="w-full pl-10 pr-10 py-3.5 bg-white rounded-2xl shadow-md border border-gray-100 text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent"
            readOnly
          />
        </form>
      </div>

      {/* Community Stats Banner */}
      {appStats && appStats.visible !== false && (
        <div className="mx-4 mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-3 flex items-center justify-around text-white shadow-md">
          <div className="text-center">
            <p className="text-lg font-extrabold">{appStats.totalUsers?.toLocaleString("en-IN")}+</p>
            <p className="text-[10px] text-blue-200 font-medium">Students</p>
          </div>
          <div className="w-px h-8 bg-white/20" />
          <div className="text-center">
            <p className="text-lg font-extrabold">{appStats.totalListings?.toLocaleString("en-IN")}+</p>
            <p className="text-[10px] text-blue-200 font-medium">Listings</p>
          </div>
          <div className="w-px h-8 bg-white/20" />
          <div className="text-center">
            <p className="text-lg font-extrabold">{appStats.totalColleges}+</p>
            <p className="text-[10px] text-blue-200 font-medium">Colleges</p>
          </div>
          <button onClick={handleShareApp} className="bg-white/20 rounded-xl px-3 py-1.5 text-xs font-bold hover:bg-white/30">
            Invite 🎉
          </button>
        </div>
      )}

      {/* ── Exam Aspirant Home ─────────────────────────────────────────────── */}
      {isExamOnly && (
        <div className="px-4 space-y-4 mt-2">
          {/* Exam Prep Zone featured card */}
          <Link to="/exam-prep" className="block bg-gradient-to-r from-orange-500 via-amber-500 to-blue-700 rounded-2xl p-5 text-white shadow-lg">
            <p className="text-orange-100 text-xs font-semibold mb-1">🎯 Your Zone</p>
            <h2 className="text-xl font-extrabold">Exam Prep Zone</h2>
            <p className="text-orange-100 text-sm mt-1">Study Material · Study Groups · Mentors · Current Affairs · Room Sharing</p>
            <div className="flex gap-2 mt-3 flex-wrap">
              {["📚 Material","👥 Groups","🧑‍🏫 Mentors","📰 Current Affairs","🏠 Rooms"].map(t => (
                <span key={t} className="bg-white/20 text-white text-xs font-semibold px-2.5 py-1 rounded-full">{t}</span>
              ))}
            </div>
            <div className="mt-3 bg-white/20 rounded-xl px-4 py-2 text-center font-bold text-sm">
              Open Exam Prep Zone →
            </div>
          </Link>

          {/* Info card */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <p className="text-amber-800 font-semibold text-sm mb-1">ℹ️ Why am I seeing this?</p>
            <p className="text-amber-700 text-xs leading-relaxed">You're registered as a Government Exam Aspirant. The College Marketplace is for college/school students only. Your content is in the Exam Prep Zone above.</p>
          </div>

          {/* MPSC/UPSC quick links */}
          <div>
            <h3 className="font-bold text-gray-800 mb-3 text-sm">⚡ Quick Access</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "📚 Study Material", path: "/exam-prep" },
                { label: "👥 Study Groups", path: "/exam-prep" },
                { label: "🧑‍🏫 Find a Mentor", path: "/exam-prep" },
                { label: "🏠 Room Sharing", path: "/exam-prep" },
                { label: "📰 Current Affairs", path: "/exam-prep" },
                { label: "💬 My Chats", path: "/chat" },
              ].map(({ label, path }) => (
                <Link key={label} to={path}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 text-sm font-medium text-gray-700 flex items-center gap-2 hover:bg-orange-50 transition">
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── College / Parent Home ──────────────────────────────────────────── */}
      {!isExamOnly && <div className="px-4 space-y-5">

        {/* Sold Success Banner */}
        {soldThisWeekCount > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-2xl px-4 py-3 flex items-center gap-3">
            <span className="text-xl">🎉</span>
            <p className="text-sm font-semibold text-green-800">
              {soldThisWeekCount} item{soldThisWeekCount !== 1 ? "s" : ""} sold this week{userProfile?.college ? ` at ${userProfile.college.split(" ").slice(0, 3).join(" ")}` : ""}!
            </p>
          </div>
        )}

        {/* Daily Deal */}
        {dailyDeal && (
          <Link to={`/listing/${dailyDeal.id}`} className="block bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl p-4 text-white shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <Zap size={16} className="text-white" />
              <span className="text-xs font-bold uppercase tracking-wide">Deal of the Day</span>
            </div>
            <div className="flex items-center gap-3">
              {dailyDeal.imageUrl && (
                <img src={dailyDeal.imageUrl} alt={dailyDeal.title} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-base line-clamp-1">{dailyDeal.title}</p>
                <p className="text-yellow-100 text-xs mt-0.5">{dailyDeal.condition} · {dailyDeal.college}</p>
                <p className="text-white font-extrabold text-lg mt-1">
                  {dailyDeal.price === 0 ? "Negotiable" : `₹${dailyDeal.price}`}
                </p>
              </div>
              <ChevronRight size={20} className="text-white flex-shrink-0" />
            </div>
          </Link>
        )}

        {/* Featured Listings */}
        {featuredListings.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <Star size={16} className="text-yellow-500 fill-yellow-500" />
                <h3 className="font-bold text-gray-800 text-base">Featured</h3>
              </div>
              <Link to="/browse" className="text-blue-600 text-xs font-medium flex items-center gap-0.5">
                See all <ChevronRight size={14} />
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar">
              {featuredListings.map(l => (
                <Link key={l.id} to={`/listing/${l.id}`} className="bg-white rounded-xl shadow-sm overflow-hidden min-w-[140px] border border-yellow-100 block flex-shrink-0">
                  <div className="h-24 bg-gray-100 overflow-hidden relative">
                    {l.imageUrl ? <img src={l.imageUrl} alt={l.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><BookOpen size={24} className="text-gray-200" /></div>}
                    <span className="absolute top-1 left-1 bg-yellow-400 text-yellow-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full">⭐ Featured</span>
                  </div>
                  <div className="p-2">
                    <p className="font-semibold text-gray-800 text-xs line-clamp-1">{l.title}</p>
                    <p className="text-blue-600 font-bold text-xs mt-0.5">{l.price === 0 ? "Free" : `₹${l.price}`}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Urgent Sales */}
        {urgentListings.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <Flame size={16} className="text-orange-500" />
                <h3 className="font-bold text-gray-800 text-base">Urgent Sales</h3>
              </div>
              <Link to="/browse" className="text-blue-600 text-xs font-medium flex items-center gap-0.5">
                See all <ChevronRight size={14} />
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar">
              {urgentListings.map(l => (
                <Link key={l.id} to={`/listing/${l.id}`} className="bg-white rounded-xl shadow-sm overflow-hidden min-w-[140px] border border-orange-100 block flex-shrink-0">
                  <div className="h-24 bg-gray-100 overflow-hidden relative">
                    {l.imageUrl ? <img src={l.imageUrl} alt={l.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><BookOpen size={24} className="text-gray-200" /></div>}
                    <span className="absolute top-1 left-1 bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">🔥 Urgent</span>
                  </div>
                  <div className="p-2">
                    <p className="font-semibold text-gray-800 text-xs line-clamp-1">{l.title}</p>
                    <p className="text-blue-600 font-bold text-xs mt-0.5">{l.price === 0 ? "Free" : `₹${l.price}`}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Rental Listings */}
        {rentalListings.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <span className="text-base">🏠</span>
                <h3 className="font-bold text-gray-800 text-base">Available for Rent</h3>
              </div>
              <Link to="/browse" onClick={() => {}} className="text-teal-600 text-xs font-medium flex items-center gap-0.5">
                See all <ChevronRight size={14} />
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar">
              {rentalListings.map(l => (
                <Link key={l.id} to={`/listing/${l.id}`} className="bg-white rounded-xl shadow-sm overflow-hidden min-w-[140px] border border-teal-100 block flex-shrink-0">
                  <div className="h-24 bg-gray-100 overflow-hidden relative">
                    {l.imageUrl
                      ? <img src={l.imageUrl} alt={l.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><span className="text-3xl">🏠</span></div>}
                    <span className="absolute top-1 left-1 bg-teal-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">🏠 Rent</span>
                  </div>
                  <div className="p-2">
                    <p className="font-semibold text-gray-800 text-xs line-clamp-1">{l.title}</p>
                    {l.rental ? (
                      <div className="flex flex-wrap gap-0.5 mt-0.5">
                        {l.rental.pricePerDay && <span className="text-[10px] font-bold text-teal-600">₹{l.rental.pricePerDay}/d</span>}
                        {l.rental.pricePerWeek && <span className="text-[10px] font-semibold text-teal-500">₹{l.rental.pricePerWeek}/wk</span>}
                        {l.rental.pricePerMonth && <span className="text-[10px] font-semibold text-teal-500">₹{l.rental.pricePerMonth}/mo</span>}
                      </div>
                    ) : (
                      <p className="text-teal-600 font-bold text-xs mt-0.5">
                        {l.price === 0 ? "Free" : `₹${l.price}`}
                        <span className="text-[10px] font-normal text-gray-400 ml-0.5">
                          {{ per_day: "/day", per_week: "/wk", per_month: "/mo" }[l.rentalPeriod] || ""}
                        </span>
                      </p>
                    )}
                    {l.rental?.availableFrom && (
                      <p className="text-[10px] text-gray-400 mt-0.5">From {new Date(l.rental.availableFrom).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {recentlyViewed.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800 text-sm">Recently Viewed</h3>
              <button onClick={() => {
                localStorage.removeItem(`cs_recently_viewed_${currentUser?.uid}`);
                setRecentlyViewed([]);
              }} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {recentlyViewed.map(l => (
                <Link key={l.id} to={`/listing/${l.id}`} className="flex-shrink-0 w-28 bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                  <div className="h-20 bg-gray-100 overflow-hidden">
                    {l.imageUrl ? <img src={l.imageUrl} alt={l.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-2xl">📦</div>}
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-semibold text-gray-800 line-clamp-1">{l.title}</p>
                    <p className="text-xs font-bold text-blue-600 mt-0.5">{l.price === 0 ? "Free" : `₹${l.price}`}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* For You — personalized */}
        {forYouListings.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <span className="text-base">✨</span>
                <h3 className="font-bold text-gray-800 text-base">Picked For You</h3>
              </div>
              <Link to="/browse" className="text-blue-600 text-xs font-medium flex items-center gap-0.5">
                See all <ChevronRight size={14} />
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar">
              {forYouListings.map(l => (
                <Link key={l.id} to={`/listing/${l.id}`} className="bg-white rounded-xl shadow-sm overflow-hidden min-w-[140px] border border-purple-100 block flex-shrink-0">
                  <div className="h-24 bg-gray-100 overflow-hidden relative">
                    {l.imageUrl
                      ? <img src={l.imageUrl} alt={l.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><BookOpen size={24} className="text-gray-200" /></div>}
                    {l.sellerVerified && <span className="absolute bottom-1 right-1 bg-green-500 text-white text-[9px] font-bold px-1 py-0.5 rounded-full">✓ ID</span>}
                  </div>
                  <div className="p-2">
                    <p className="font-semibold text-gray-800 text-xs line-clamp-1">{l.title}</p>
                    <p className="text-blue-600 font-bold text-xs mt-0.5">{l.price === 0 ? "Free" : `₹${l.price}`}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Categories Grid */}
        <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-800 text-base">🗂 Categories</h3>
            <Link to="/browse" className="text-blue-600 text-xs font-semibold bg-blue-50 px-2 py-1 rounded-lg">See all</Link>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {CATEGORIES.map(({ name, icon: Icon, color }) => (
              <Link
                key={name}
                to={`/browse?category=${encodeURIComponent(name)}`}
                className="flex flex-col items-center gap-1.5 group"
              >
                <div className={`w-13 h-13 w-12 h-12 rounded-2xl flex items-center justify-center ${color} group-active:scale-95 transition shadow-sm`}>
                  <Icon size={20} />
                </div>
                <span className="text-xs text-gray-600 text-center leading-tight">{name}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h3 className="font-bold text-gray-800 mb-3 text-base">⚡ What do you need?</h3>
          <div className="grid grid-cols-4 gap-2">
            {visibleActions.map(({ label, icon: Icon, color, path }) => (
              <Link
                key={label}
                to={path}
                className={`flex flex-col items-center gap-1.5 p-2.5 rounded-2xl border shadow-sm active:scale-95 transition ${color}`}
              >
                <Icon size={22} />
                <span className="text-xs font-medium text-center leading-tight">{label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Parent Zone Banner */}
        <Link to="/parent-zone" className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-4 text-white flex items-center justify-between">
          <div>
            <p className="font-bold text-lg">Parent Zone</p>
            <p className="text-amber-100 text-xs mt-0.5">School books, uniforms, bags & more</p>
            <p className="text-amber-100 text-xs">For parents of school students</p>
          </div>
          <div className="text-4xl">🎒</div>
        </Link>


        {/* Latest Listings */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-800 text-base">🛍 Latest Listings</h3>
            <Link to="/browse" className="text-blue-600 text-xs font-medium flex items-center gap-0.5">
              See all <ChevronRight size={14} />
            </Link>
          </div>
          {loadingListings ? (
            <div className="grid grid-cols-2 gap-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden animate-pulse">
                  <div className="h-32 bg-gray-200" />
                  <div className="p-3 space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-xl">
              <BookOpen size={36} className="mx-auto mb-2 text-gray-200" />
              <p className="text-gray-500 text-sm">No listings yet on your campus.</p>
              <Link
                to="/sell"
                className="mt-3 inline-block bg-blue-600 text-white px-5 py-2 rounded-xl text-sm font-semibold"
              >
                Post the First Listing
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {listings.map(l => <ListingCard key={l.id} listing={l} />)}
            </div>
          )}
        </div>

        {/* Loyalty Rewards */}
        {(() => {
          const totalListings = userProfile?.totalListings || 0;
          const totalSales = userProfile?.totalSales || 0;
          const points = totalListings + totalSales * 2;
          let tier = "Bronze", nextTier = "Silver", needed = 10 - points, tierColor = "from-amber-600 to-amber-800", badge = "🥉";
          if (points >= 30) { tier = "Gold"; nextTier = null; tierColor = "from-yellow-400 to-yellow-600"; badge = "🥇"; }
          else if (points >= 10) { tier = "Silver"; nextTier = "Gold"; needed = 30 - points; tierColor = "from-gray-400 to-gray-600"; badge = "🥈"; }
          const progress = tier === "Bronze" ? Math.min(points / 10, 1) : tier === "Silver" ? Math.min((points - 10) / 20, 1) : 1;
          return (
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-4 text-white">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-indigo-200 text-xs font-semibold uppercase tracking-wide">Your Loyalty Tier</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-2xl">{badge}</span>
                    <p className="text-xl font-extrabold">{tier} Member</p>
                  </div>
                </div>
                <Link to="/my-listings" className="bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-xl">View Perks</Link>
              </div>
              <div className="bg-white/20 rounded-full h-2 mb-1.5">
                <div className="bg-white rounded-full h-2 transition-all" style={{ width: `${progress * 100}%` }} />
              </div>
              <p className="text-indigo-200 text-xs">
                {nextTier ? `${needed} more point${needed !== 1 ? "s" : ""} to reach ${nextTier}` : "You've reached the highest tier!"}
                {" · "}+1 per listing, +2 per sale
              </p>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {[
                  { label: "Priority listing", active: points >= 0 },
                  { label: "Verified badge boost", active: points >= 10 },
                  { label: "Featured listing", active: points >= 30 },
                ].map(({ label, active }) => (
                  <div key={label} className={`rounded-xl px-2 py-2 text-center text-xs font-semibold ${active ? "bg-white/30 text-white" : "bg-white/10 text-indigo-300 line-through"}`}>
                    {label}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Active Swaps */}
        {swaps.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800 text-base">🔄 Active Swaps</h3>
              <Link to="/swap" className="text-blue-600 text-xs font-medium flex items-center gap-0.5">
                See all <ChevronRight size={14} />
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {swaps.map(s => <SwapCard key={s.id} swap={s} />)}
            </div>
          </div>
        )}

        {/* Free Giveaways */}
        {giveaways.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800 text-base">🎁 Free Giveaways</h3>
              <Link to="/giveaway" className="text-blue-600 text-xs font-medium flex items-center gap-0.5">
                See all <ChevronRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {giveaways.map(item => (
                <div key={item.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="h-28 bg-gray-100">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl text-gray-200">
                        <Gift size={32} className="text-gray-200" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">FREE</span>
                    <p className="font-semibold text-gray-800 text-sm mt-1.5 line-clamp-1">{item.title}</p>
                    <p className="text-gray-400 text-xs mt-0.5 line-clamp-1">{item.postedByName}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>}
    </div>
  );
}
